import {useState, useEffect, useRef} from 'react';
import { Stack, Grid, Dialog, DialogTitle, DialogContent, TextField, DialogActions, DialogContentText, Box, FormControl, InputLabel, MenuItem, Button, Snackbar, Alert} from '@mui/material';
import Select from '@mui/material/Select';
import generateUID from '../utils/roomID'
import './styles/Home.css';
import { useForm } from "react-hook-form";
import { useHistory } from 'react-router-dom';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

export default function CreateRoomButton({socket}) {
    const [open, setOpen] = useState(false)
    const [error, setError] = useState('');
    // Tracks the "server didn't respond" timeout so the listener can cancel it on any reply.
    const timerRef = useRef(null);
    const handleClose = () => {setOpen(false)};
    const history = useHistory();

    useEffect(() => {
        const handler = (data) => {
            // Any reply (success or server-side error) means the server is alive — cancel timeout.
            if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
            if (data.status === 'success') {
                // redirect to /game/{roomID}
                history.push({
                    pathname: `/game/${data.roomID}`,
                    state: {...data, player: 'p1'}})
            } else {
                // show the server's error in a snackbar; keep the dialog open to retry
                setError(data.error)
            }
        };
        socket.on('room_created', handler);
        return () => {
            socket.off('room_created', handler);
            if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
        };
    }, [socket, history])

    return (<>
    <Button className='btn' color='inherit' variant="outlined" onClick={() => setOpen(true)} endIcon={<AddCircleOutlineIcon/>}>
        Create Room
    </Button>
      <Dialog
            fullWidth={true}
            maxWidth="sm"
            open={open}
            onClose={handleClose}
        >
            <DialogTitle>Design Your Game</DialogTitle>
            <DialogContent sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
                <DialogContentText>
                    You can choose modes, colors, and time controls by yourself.
                </DialogContentText>
                <Box sx={{ mt: 2, mb: 0 }}>
                    <GameSetting socket={socket} setError={setError} timerRef={timerRef}/>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>

        <Snackbar
            open={!!error}
            autoHideDuration={4000}
            onClose={() => setError('')}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
            <Alert severity="error" variant="filled" onClose={() => setError('')} sx={{ width: '100%' }}>
                {error}
            </Alert>
        </Snackbar>

        </>)

}

export function GameSetting({socket, setError, timerRef}) {
    const Modes = ['Blindfold', 'Normal', 'Custom'];
    const Colors = ['White', 'Black', 'Random'];
    const Hints = ['One', 'Two', 'Three', 'Four', 'Five', 'Ten'];
    const timeControls = ['3 v 3', '5 v 5', '10 v 10']

    const [username, setUsername] = useState('');
    const [hint1, setHint1] = useState('One')
    const [hint2, setHint2] = useState('One');
    const [gameMode1, setGameMode1] = useState('Normal');
    const [gameMode2, setGameMode2] = useState('Normal');
    const [color, setColor] = useState('White');
    const [timeControl, setTimeControl] = useState('5 v 5');
    const { handleSubmit } = useForm();

    const onSubmit = () => {
        const data = {p1Mode: gameMode1,
        p2Mode: gameMode2,
        p1Hint: hint1,
        p2Hint: hint2,
        p1Color: color.toLowerCase(),
        timeControl: timeControl,
        username: username.length > 0 ? username : "Player 1",
        roomID: generateUID()
    };

        // Don't silently fail when the socket is disconnected (e.g. backend down).
        if (!socket.connected) {
            setError("Can't reach the server. Please try again in a moment.");
            return;
        }

        socket.emit("create_room", data);

        // If the server never replies (transient hang), surface that too.
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            setError("Server didn't respond. Please try again.");
            timerRef.current = null;
        }, 5000);
    }



    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            {/* The Grid container wraps all form elements */}
            <Grid container spacing={2}>

                {/* ===== YOUR SETTINGS ===== */}
                <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                        <FormControl fullWidth>
                            <InputLabel>Your Mode</InputLabel>
                            <Select value={gameMode1} label="Your Mode" onChange={(e) => setGameMode1(e.target.value)}>
                                {Modes.map((mode) => <MenuItem key={`p1-${mode}`} value={mode}>{mode}</MenuItem>)}
                            </Select>
                        </FormControl>
                        {gameMode1 === 'Custom' && (
                            <FormControl fullWidth>
                                <InputLabel>Hints Allowed</InputLabel>
                                <Select value={hint1} label="Hints Allowed" onChange={(e) => setHint1(e.target.value)}>
                                    {Hints.map((hint) => <MenuItem key={`p1-hint-${hint}`} value={hint}>{hint}</MenuItem>)}
                                </Select>
                            </FormControl>
                        )}
                    </Stack>
                </Grid>

                {/* ===== OPPONENT SETTINGS ===== */}
                <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                        <FormControl fullWidth>
                            <InputLabel>Opponent's Mode</InputLabel>
                            <Select value={gameMode2} label="Opponent's Mode" onChange={(e) => setGameMode2(e.target.value)}>
                                {Modes.map((mode) => <MenuItem key={`p2-${mode}`} value={mode}>{mode}</MenuItem>)}
                            </Select>
                        </FormControl>
                        {gameMode2 === 'Custom' && (
                            <FormControl fullWidth>
                                <InputLabel>Hints Allowed</InputLabel>
                                <Select value={hint2} label="Hints Allowed" onChange={(e) => setHint2(e.target.value)}>
                                    {Hints.map((hint) => <MenuItem key={`p2-hint-${hint}`} value={hint}>{hint}</MenuItem>)}
                                </Select>
                            </FormControl>
                        )}
                    </Stack>
                </Grid>

                {/* ===== GAME SETTINGS ===== */}
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <InputLabel>Your Color</InputLabel>
                        <Select value={color} label="Your Color" onChange={(e) => setColor(e.target.value)}>
                            {Colors.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <InputLabel>Time Control</InputLabel>
                        <Select value={timeControl} label="Time Control" onChange={(e) => setTimeControl(e.target.value)}>
                            {timeControls.map((time) => <MenuItem key={time} value={time}>{time}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>

                {/* ===== USERNAME & SUBMIT ===== */}
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Your Name"
                        placeholder="Player 1"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        helperText={username.length > 0 ? `Good luck, ${username}!` : ''}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Button fullWidth size="large" type="submit" variant="contained">
                        Create Room
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
}